import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  FeatureQueryResponse,
  MapConfigResponse,
  RouteLocation,
  SearchResultsQueryResponse,
  ConfigFeatureNamesResponse,
  EventPostResponse,
  RouteOptions,
  RouteQueryResponse,
  SessionPostResponse,
  GeofenceQueryResponse,
  ConfigFeatureObjectResponse,
} from "./types";
import { AnalyticsEvents } from "../../utils/analyticsTypes";

import { PROJECT } from "../../constants";

export interface GetFeaturesByNameQueryParams {
  longName: string;
  project?: string;
  lang?: string;
  longitude?: number;
  latitude?: number;
  floor_id?: number;
}

export const getFeaturesByNameQuery = ({
  longName,
  project,
  lang,
  latitude,
  longitude,
  floor_id,
}: GetFeaturesByNameQueryParams) => ({
  url: `/v1/maps/${project}/features`,
  params: { long_name: longName, lang, latitude, longitude, floor_id },
});

export const vectorMapsAPI = createApi({
  reducerPath: "vectorMapsApi",
  tagTypes: [
    "Config",
    "Feature",
    "SearchResults",
    "FeatureNames",
    "Route",
    "Geofences",
    "QuickSearchFeatures",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : "",
  }),
  endpoints: (builder) => ({
    getMapConfig: builder.query<MapConfigResponse, void>({
      query: () =>
        PROJECT
          ? `/v1/maps/${PROJECT}`
          : `/v1/maps?host=${encodeURIComponent(window.location.hostname)}`,
      providesTags: ["Config"],
    }),
    getFeatureById: builder.query<
      FeatureQueryResponse,
      { featureId: string; project?: string }
    >({
      query: ({ featureId, project }) =>
        `/v1/maps/${project}/features/${featureId}`,
      providesTags: ["Feature"],
    }),
    getFeatureByUid: builder.query<
      FeatureQueryResponse,
      { featureUid: number; project?: string }
    >({
      query: ({ featureUid, project }) =>
        `/v1/maps/${project}/features-uid/${featureUid}`,
      providesTags: ["Feature"],
    }),
    getFeaturesBySearch: builder.query<
      SearchResultsQueryResponse,
      {
        query: string;
        limit?: number;
        longitude?: number;
        latitude?: number;
        floor_id?: number;
        project?: string;
        lang?: string;
      }
    >({
      query: ({
        query,
        latitude,
        limit = 50,
        longitude,
        project,
        floor_id,
        lang,
      }) => ({
        url: `/v1/maps/${project}/search`,
        params: { query, latitude, longitude, floor_id, limit, lang },
      }),
      providesTags: ["SearchResults"],
    }),
    getFeaturesByTagSearch: builder.query<
      SearchResultsQueryResponse,
      {
        id: string;
        bespoke: boolean;
        limit?: number;
        longitude?: number;
        latitude?: number;
        floor_id?: number;
        project?: string;
      }
    >({
      query: ({
        id,
        latitude,
        limit = 50,
        longitude,
        floor_id,
        project,
        bespoke,
      }) => ({
        url: bespoke
          ? `/v1/maps/${project}/search/tag/bespoke/${id}`
          : `/v1/maps/${project}/search/tag/${id}`,
        params: { latitude, longitude, limit, floor_id },
      }),
      providesTags: ["SearchResults"],
    }),
    getFeaturesByName: builder.query<
      SearchResultsQueryResponse,
      GetFeaturesByNameQueryParams
    >({
      query: getFeaturesByNameQuery,
      providesTags: ["SearchResults"],
    }),
    getSearchFeatureNames: builder.query<
      ConfigFeatureNamesResponse,
      { project?: string; lang?: string }
    >({
      query: ({ project, lang }) => ({
        url: `/v1/maps/${project}/feature-names`,
        params: { lang },
      }),
      providesTags: ["FeatureNames"],
    }),
    getQuickSearchFeatures: builder.query<
      ConfigFeatureObjectResponse,
      { project?: string; lang?: string }
    >({
      query: ({ project, lang }) => ({
        url: `/v1/maps/${project}/feature-objects`,
        params: { lang },
      }),
      providesTags: ["QuickSearchFeatures"],
    }),
    postRoute: builder.query<
      RouteQueryResponse,
      {
        version: string;
        from: RouteLocation;
        to: RouteLocation;
        via?: RouteLocation[];
        options?: RouteOptions;
        project?: string;
      }
    >({
      query: ({ from, to, via, options, project, version }) => ({
        url: `/${version}/route`,
        method: "POST",
        body: { from, to, via, options, project },
      }),
      providesTags: ["Route"],
    }),
    postSession: builder.mutation<
      SessionPostResponse,
      {
        project?: string;
      }
    >({
      query: ({ project }) => ({
        url: `/v1/maps/${project}/sessions`,
        method: "POST",
        body: { component: "map_ui" },
      }),
    }),
    postEvent: builder.mutation<
      EventPostResponse,
      {
        project?: string;
        uuid?: string;
        data: AnalyticsEvents;
      }
    >({
      query: ({ project, uuid, data }) => ({
        url: `/v1/maps/${project}/sessions/${uuid}/events`,
        method: "POST",
        body: {
          event_type: data.event_type,
          ...("event_data" in data && { event_data: data.event_data }),
        },
      }),
    }),
    getGeofences: builder.query<
      GeofenceQueryResponse,
      { project?: string; limit?: number }
    >({
      query: ({ project, limit }) => ({
        url: `/v1/maps/${project}/geofences`,
        params: { limit },
      }),
      providesTags: ["Geofences"],
    }),
  }),
});

export const {
  useGetMapConfigQuery,
  useGetFeatureByIdQuery,
  useGetFeatureByUidQuery,
  useGetFeaturesByTagSearchQuery,
  useGetFeaturesBySearchQuery,
  useGetFeaturesByNameQuery,
  useGetSearchFeatureNamesQuery,
  useGetQuickSearchFeaturesQuery,
  usePostRouteQuery,
  usePostSessionMutation,
  usePostEventMutation,
  useGetGeofencesQuery,
} = vectorMapsAPI;

export default vectorMapsAPI;
